import { createContext, useContext, type ReactElement } from "react";
import * as RadixTabs from "@radix-ui/react-tabs";
import { cn } from "@/lib/cn";
import type { TabsContentProps, TabsListProps, TabsProps, TabsTriggerProps } from "./Tabs.types";

type TabsStyleContextValue = {
  variant: NonNullable<TabsProps["variant"]>;
  size: NonNullable<TabsProps["size"]>;
};

const TabsStyleContext = createContext<TabsStyleContextValue>({ variant: "line", size: "md" });

const triggerSizeClassName = {
  xs: "bfrs-min-h-[38px] bfrs-px-[14px] bfrs-text-[13px]",
  sm: "bfrs-min-h-8 bfrs-px-3 bfrs-text-xs",
  md: "bfrs-min-h-10 bfrs-px-[14px] bfrs-text-[13px]",
  lg: "bfrs-min-h-11 bfrs-px-5 bfrs-text-sm"
} as const;

function TabsList({ className, ...props }: TabsListProps) {
  const { variant } = useContext(TabsStyleContext);

  const list = (
    <RadixTabs.List
      className={cn(
        "bfrs-flex bfrs-gap-1 bfrs-overflow-x-auto bfrs-bg-surface-card",
        variant === "line"
          ? "bfrs-border-b bfrs-border-border bfrs-px-5"
          : "bfrs-rounded-[var(--bfrs-tabs-radius)] bfrs-bg-surface-muted bfrs-p-1",
        className
      )}
      {...props}
    />
  );

  if (variant !== "line") {
    return list;
  }

  // The bottom divider should reach the parent's full width, ignoring the parent's
  // horizontal padding. A 1px line bleeds past the list on each side by
  // `--bfrs-tabs-divider-bleed` (default 16px = the default Card padding). It sits
  // behind the list (isolated stacking context) so the list's own border-b draws the
  // line under the tabs and the active tab underline still paints on top, while the
  // overhang extends the line to the parent edges. Only the line bleeds — the list
  // background stays within the content area. Override the variable to match a
  // different parent padding (e.g. 22px for a bordered Card, 24px for a page section).
  return (
    <div className="bfrs-relative bfrs-isolate">
      {list}
      <span
        aria-hidden="true"
        className="bfrs-pointer-events-none bfrs-absolute bfrs-bottom-0 bfrs-z-[-1] bfrs-h-px bfrs-bg-border bfrs-left-[calc(-1*var(--bfrs-tabs-divider-bleed,16px))] bfrs-right-[calc(-1*var(--bfrs-tabs-divider-bleed,16px))]"
      />
    </div>
  );
}

TabsList.displayName = "Tabs.List";

function TabsTrigger({ className, badge, children, ...props }: TabsTriggerProps) {
  const { variant, size } = useContext(TabsStyleContext);

  return (
    <RadixTabs.Trigger
      className={cn(
        "bfrs-inline-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-2 bfrs-whitespace-nowrap bfrs-border-b-2 bfrs-border-transparent bfrs-font-medium bfrs-text-text-muted bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus disabled:bfrs-pointer-events-none disabled:bfrs-opacity-50",
        triggerSizeClassName[size],
        variant === "line"
          ? "data-[state=active]:bfrs-border-primary data-[state=active]:bfrs-font-semibold data-[state=active]:bfrs-text-primary"
          : "bfrs-rounded-md bfrs-border-b-0 data-[state=active]:bfrs-bg-surface-card data-[state=active]:bfrs-font-semibold data-[state=active]:bfrs-text-text-primary data-[state=active]:bfrs-shadow-low",
        className
      )}
      {...props}
    >
      {children}
      {badge}
    </RadixTabs.Trigger>
  );
}

TabsTrigger.displayName = "Tabs.Trigger";

function TabsContent({ className, ...props }: TabsContentProps) {
  return <RadixTabs.Content className={cn("bfrs-pt-5 bfrs-outline-none", className)} {...props} />;
}

TabsContent.displayName = "Tabs.Content";

function TabsRoot({ items, variant = "line", size = "md", className, listClassName, triggerClassName, contentClassName, children, ...props }: TabsProps) {
  return (
    <TabsStyleContext.Provider value={{ variant, size }}>
      <RadixTabs.Root className={cn("bfrs-w-full", className)} {...props}>
        {items ? (
          <>
            <TabsList className={listClassName}>
              {items.map((item) => (
                <TabsTrigger key={item.value} value={item.value} disabled={item.disabled} badge={item.badge} className={triggerClassName}>
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {items.map((item) => (
              <TabsContent key={item.value} value={item.value} className={contentClassName}>
                {item.content}
              </TabsContent>
            ))}
          </>
        ) : (
          children
        )}
      </RadixTabs.Root>
    </TabsStyleContext.Provider>
  );
}

TabsRoot.displayName = "Tabs";

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent
}) as ((props: TabsProps) => ReactElement) & {
  List: typeof TabsList;
  Trigger: typeof TabsTrigger;
  Content: typeof TabsContent;
};
